"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { Upload, FileCheck } from "lucide-react";
import { registerWhitelistRecipientAction, uploadDocumentAction } from "@/app/actions";

export default function WhitelistForm({ customerId }: { customerId: string }) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Array<{ pointer?: string; detail?: string }>>([]);
  const [done, setDone] = useState<{ id: string; status: string } | null>(null);
  const [docs, setDocs] = useState<Array<{ id: string; fileName: string }>>([]);

  const [f, setF] = useState({
    rail: "us" as "us" | "swift",
    routingNumber: "",
    accountNumber: "",
    bic: "",
    swiftIdType: "iban" as "iban" | "accountNumber",
    iban: "",
    relationship: "group_entity" as "self" | "group_entity",
    legalName: "",
    label: "",
  });

  function set<K extends keyof typeof f>(k: K, v: (typeof f)[K]) {
    setF((x) => ({ ...x, [k]: v }));
  }

  async function onUpload(files: FileList | null) {
    if (!files) return;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.set("file", file);
      fd.set("purpose", "transaction_support");
      fd.set("name", file.name);
      const res = await uploadDocumentAction(fd);
      if (res.ok) setDocs((d) => [...d, { id: res.data.id, fileName: res.data.fileName ?? file.name }]);
      else setError(`${file.name}: ${res.error}`);
    }
  }

  function submit() {
    setError(null);
    setFieldErrors([]);
    const body =
      f.rail === "us"
        ? {
            rail: "us",
            routingNumber: f.routingNumber,
            accountNumber: f.accountNumber,
            relationship: f.relationship,
            legalName: f.legalName,
            evidenceDocumentIds: docs.map((d) => d.id),
            ...(f.label ? { label: f.label } : {}),
          }
        : {
            rail: "swift",
            bic: f.bic,
            ...(f.swiftIdType === "iban" ? { iban: f.iban } : { accountNumber: f.accountNumber }),
            relationship: f.relationship,
            legalName: f.legalName,
            evidenceDocumentIds: docs.map((d) => d.id),
            ...(f.label ? { label: f.label } : {}),
          };
    start(async () => {
      const res = await registerWhitelistRecipientAction(customerId, body);
      if (res.ok) {
        const d = res.data as { id: string; status: string };
        setDone({ id: d.id, status: d.status });
        router.refresh();
      } else {
        setError(res.error);
        setFieldErrors(res.fieldErrors ?? []);
      }
    });
  }

  if (done) {
    return (
      <div className="card-green rise max-w-xl p-8">
        <div className="display text-[24px]">Recipient submitted</div>
        <p className="mt-1 text-[14px] font-light">
          {done.id} is <strong>{done.status.replaceAll("_", " ")}</strong>. Conduit's compliance team reviews the
          evidence; check back on this page for the outcome.
        </p>
      </div>
    );
  }

  return (
    <div className="card rise max-w-xl p-8">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="label">Rail</label>
          <div className="flex gap-2">
            {(["us", "swift"] as const).map((r) => (
              <button
                key={r}
                className="pill"
                style={
                  f.rail === r
                    ? { background: "var(--c-charcoal)", color: "var(--c-white)" }
                    : { boxShadow: "inset 0 0 0 1px var(--c-hairline)", color: "var(--c-ink-soft)" }
                }
                onClick={() => set("rail", r)}
              >
                {r === "us" ? "domestic · us (routing + account)" : "international · swift (bic + iban/account)"}
              </button>
            ))}
          </div>
          {f.rail === "swift" && (
            <p className="mt-2 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
              SWIFT whitelisting requires the recipient bank's BIC, plus either an IBAN or an account number — use
              account number for banks in countries without IBANs (e.g. Hong Kong, most of Asia).
            </p>
          )}
        </div>

        <div className="col-span-2">
          <label className="label">Legal name of account holder</label>
          <input className="field" value={f.legalName} onChange={(e) => set("legalName", e.target.value)} />
        </div>

        {f.rail === "us" ? (
          <>
            <div>
              <label className="label">Routing number (ABA)</label>
              <input className="field mono" value={f.routingNumber} onChange={(e) => set("routingNumber", e.target.value)} />
            </div>
            <div>
              <label className="label">Account number</label>
              <input className="field mono" value={f.accountNumber} onChange={(e) => set("accountNumber", e.target.value)} />
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="label">BIC / SWIFT code</label>
              <input className="field mono" value={f.bic} onChange={(e) => set("bic", e.target.value)} />
            </div>
            <div>
              <label className="label">Identify by</label>
              <div className="flex gap-2">
                {(["iban", "accountNumber"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="pill"
                    style={
                      f.swiftIdType === t
                        ? { background: "var(--c-charcoal)", color: "var(--c-white)" }
                        : { boxShadow: "inset 0 0 0 1px var(--c-hairline)", color: "var(--c-ink-soft)" }
                    }
                    onClick={() => set("swiftIdType", t)}
                  >
                    {t === "iban" ? "iban" : "account number"}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <label className="label">{f.swiftIdType === "iban" ? "IBAN" : "Account number"}</label>
              {f.swiftIdType === "iban" ? (
                <input className="field mono" value={f.iban} onChange={(e) => set("iban", e.target.value)} />
              ) : (
                <input className="field mono" value={f.accountNumber} onChange={(e) => set("accountNumber", e.target.value)} />
              )}
            </div>
          </>
        )}

        <div>
          <label className="label">Relationship</label>
          <select className="field" value={f.relationship} onChange={(e) => set("relationship", e.target.value as "self" | "group_entity")}>
            <option value="group_entity">group entity</option>
            <option value="self">self (own account)</option>
          </select>
        </div>
        <div>
          <label className="label">Label (optional)</label>
          <input className="field" value={f.label} onChange={(e) => set("label", e.target.value)} />
        </div>

        <div className="col-span-2">
          <label className="label">Evidence of the relationship (at least one)</label>
          <p className="mb-2 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
            e.g. an ownership/org chart, an intercompany agreement, or a bank statement showing the same corporate
            group.
          </p>
          <label
            className="flex cursor-pointer items-center gap-3 rounded-[16px] border border-dashed p-4"
            style={{ borderColor: "var(--c-hairline)" }}
          >
            <Upload size={16} />
            <span className="text-[14px] font-light">Upload PDF, JPG or PNG (max 10 MB)</span>
            <input type="file" multiple hidden accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => onUpload(e.target.files)} />
          </label>
          {docs.map((d) => (
            <div key={d.id} className="mt-2 flex items-center gap-2 text-[13px] font-light">
              <FileCheck size={14} /> {d.fileName}
            </div>
          ))}
        </div>
      </div>

      <button
        className="btn btn-primary mt-6 w-full"
        disabled={
          pending ||
          !f.legalName ||
          docs.length === 0 ||
          (f.rail === "us"
            ? !f.routingNumber || !f.accountNumber
            : !f.bic || (f.swiftIdType === "iban" ? !f.iban : !f.accountNumber))
        }
        onClick={submit}
      >
        {pending ? "Submitting…" : "Submit for whitelisting"}
      </button>

      {error && (
        <div className="card-cream mt-4 p-4 text-[14px] font-light" style={{ color: "var(--c-danger)" }}>
          {error}
          {fieldErrors.length > 0 && (
            <ul className="mt-2 list-inside list-disc" style={{ color: "var(--c-charcoal)" }}>
              {fieldErrors.map((fe, i) => (
                <li key={i}>
                  <span className="mono text-[12px]">{fe.pointer}</span> {fe.detail}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
